<template>
    <div class="instrument-detail-page overview-page" style="margin-top: 8px">
      <EntityOverviewLayout
        v-if="instrument"
        :panelTabs="overviewPanels"
        defaultPanel="files"
      >
        <template #top-left>
          <InstrumentDetails
            :instrument="instrument"
            :showMetadata="false"
            @updateInstrument="updateInstrumentDetails"
          />
        </template>

        <template #top-right>
          <DisplayImageGallery entityType="instrument" :entityId="instrument.id" />
        </template>

        <template #bottom-left>
          <NodeMdList
            :key="instrument.node_id"
            :mdListId="instrument.metadata_list_id"
            :assType="'instrument'"
            :assId="instrument.node_id"
            @mdListCreated="handleMdListCreated"
            @mdListDetached="handleMdListDetached"
          />
        </template>

        <template #panel-files>
          <Files
            ref="filesComp"
            :files="files"
            :experimentId="instrument.id"
            :assId="instrument.id"
            :assType="'instrument'"
            :isUploading="isUploading"
            :uploadPercent="uploadPercent"
            :uploadLabel="uploadLabel"
            @addFiles="addFiles"
            @removeFile="removeFile"
          />
        </template>

        <template #panel-comments>
          <Comments
            :comments="comments"
            :users="users"
            :files="files"
            :entityId="instrument.id"
            @addComment="addComment"
            @removeComment="removeComment"
            @updateComment="handleUpdateComment"
          />
        </template>
      </EntityOverviewLayout>

      <FileUploadSession
        :visible="uploadSession.visible"
        :fileType="uploadSession.fileType"
        :entityId="uploadSession.entityId"
        :files="uploadSession.files"
        :entraId="loggedInUser?.entra_id"
        :folderId="uploadSession.folderId"
        @complete="handleUploadComplete"
        @close="handleUploadClose"
        @error="handleUploadError"
      />
    </div>
  </template>
  
  
  <script>
  import InstrumentDetails from '@/components/InstrumentDetails.vue';
  import Files from '@/components/ExperimentFiles.vue';
  import Comments from '@/components/ExperimentComments.vue';
  import NodeMdList from '@/components/NodeMdList.vue';
  import FileUploadSession from '@/components/FileUploadSession.vue';
  import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
  import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
  import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
  import axios from 'axios';
  import { API_ENDPOINTS } from '@/services/config';
  import apiService from '@/services/apiService';
  import { useToast } from 'vue-toastification';
  import { mapState } from 'vuex';
  
  export default {
    components: {
      InstrumentDetails,
      Files,
      Comments,
      NodeMdList,
      FileUploadSession,
      EntityOverviewLayout,
      DisplayImageGallery,
    },
    data() {
      return {
        instrument: null,
        files: [],
        comments: [],
        users: [],
        isUploading: false,
        uploadPercent: 0,
        uploadLabel: "",
        uploadSession: {
          visible: false,
          fileType: '',
          entityId: null,
          files: [],
          folderId: null,
        },
      };
    },
    setup() {
      const toast = useToast();
      const discussion = useEntityDiscussion();
      return { toast, discussion };
    },
    created() {
      this.fetchInstrumentData();
      this.fetchComments();
      this.fetchUsers();
    },
    computed: {
    ...mapState(['loggedInUser']),
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
      ];
    },
  },
    methods: {
      async handleMdListCreated(newMdList) {
        const mdListId = newMdList?.id ?? null;
        if (!this.instrument || !mdListId) {
          return;
        }

        await this.updateInstrumentDetails({
          ...this.instrument,
          metadata_list_id: mdListId,
        }, {
          successMessage: 'Template attached successfully!',
          errorMessage: 'Failed to attach template',
        });
      },
      async handleMdListDetached() {
        if (!this.instrument) {
          return;
        }

        await this.updateInstrumentDetails({
          ...this.instrument,
          metadata_list_id: null,
        }, {
          successMessage: 'Template removed successfully!',
          errorMessage: 'Failed to remove template',
        });
      },
      async fetchInstrumentData() {
        const instrumentId = this.$route.params.id;
        try {
          const response = await axios.get(API_ENDPOINTS.instrument(instrumentId));
          this.instrument = response.data;
          this.fetchFiles(); // Fetch files after setting instrument
        } catch (error) {
          this.toast.error('Failed to fetch instrument data');
          console.error('Failed to fetch instrument data:', error);
        }
      },
      async fetchFiles() {
        const instrumentId = this.instrument.id;
        try {
          const response = await axios.get(API_ENDPOINTS.files, {
            params: {
              file_type: 'instrument',
              entity_id: instrumentId,
              entra_id: this.loggedInUser.entra_id
            },
          });
          this.files = response.data;
        } catch (error) {
          if (error.response && error.response.status !== 404) {
            this.toast.error('Failed to fetch files');
            console.error('Failed to fetch files:', error);
          }
        }
      },
      async fetchComments() {
        try {
          this.comments = await this.discussion.fetchComments({
            entityType: 'instrument',
            entityId: this.$route.params.id,
            entraId: this.loggedInUser.entra_id,
          });
        } catch (error) {
          this.toast.error('Failed to fetch comments');
          console.error('Failed to fetch comments:', error);
        }
      },
      async fetchUsers() {
        try {
          this.users = await this.discussion.fetchUsers({
            entraId: this.loggedInUser.entra_id,
          });
        } catch (error) {
          this.toast.error('Failed to fetch users');
          console.error('Failed to fetch users:', error);
        }
      },
      async addFiles({ files, id, folderId }) {
        this.uploadSession = {
          visible: true,
          fileType: 'instrument',
          entityId: id,
          files: Array.from(files),
          folderId: folderId || null,
        };
      },

      async handleUploadComplete() {
        await this.fetchFiles();
        if (this.$refs.filesComp?.fetchFolders) {
          await this.$refs.filesComp.fetchFolders();
        }
        this.uploadSession.visible = false;
      },

      handleUploadClose() {
        this.uploadSession.visible = false;
        this.fetchFiles();
      },

      handleUploadError(error) {
        console.error('Upload session error:', error);
      },

      async removeFile(fileId) {
        try {
          await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
          this.files = this.files.filter(file => file.id !== fileId);
          this.toast.success('File removed successfully!');
        } catch (error) {
          this.toast.error('Failed to remove file');
          console.error('Failed to remove file:', error);
        }
      },
      async addComment(commentText, author_id, files) {
        try {
          await this.discussion.addComment({
            entityType: 'instrument',
            entityId: this.$route.params.id,
            text: commentText,
            authorId: author_id,
            files,
            entraId: this.loggedInUser.entra_id,
          });

          if (files && files.length > 0) {
            await this.fetchFiles();
          }
          await this.fetchComments();
          this.toast.success('Comment added successfully!');
        } catch (error) {
          this.toast.error('Failed to add comment');
          console.error('Failed to add comment:', error);
        }
      },
      async removeComment(commentId) {
        try {
          await this.discussion.removeComment({
            commentId,
            userId: this.loggedInUser.id,
            entraId: this.loggedInUser.entra_id,
          });
          await this.fetchComments();
          await this.fetchFiles();
          this.toast.success('Comment removed successfully!');
        } catch (error) {
          this.toast.error('Failed to remove comment');
          console.error('Failed to remove comment:', error);
        }
      },
      async updateInstrumentDetails(updatedInstrument, options = {}) {
        const instrumentId = this.$route.params.id;
        const {
          successMessage = 'Instrument updated successfully!',
          errorMessage = 'Failed to update instrument',
        } = options;

        try {
            const response = await apiService.updateResource(
              API_ENDPOINTS.instrument(instrumentId),
              updatedInstrument,
              this.loggedInUser?.id,
              this.loggedInUser?.entra_id
            );
            this.instrument = response.data;
            await this.fetchInstrumentData();
            this.toast.success(successMessage);
        } catch (error) {
            this.toast.error(errorMessage);
            console.error('Failed to update instrument:', error);
        }
      },
      async handleUpdateComment(commentId, newText, Id) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'instrument',
          entityId: Id ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },
    },
  };
  </script>

<style scoped lang="scss">
.overview-page {
  padding-bottom: 12px;
}
</style>

  
  